import React from 'react';
import Alias from "components/controls/alias";
import WorkNodeBase from '../common/workNodeBase';
import CellsSurface from '../surfaces/cells/cellsSurface';
import { LOADING_MISSIONS, LOADING_CELLS } from '../surfaces/cells/boardWaits';
import WidgetStatus from '../../common/widgetStatus';
import {
  createStore,
  setMissions,
  setCells,
  setDependsOn,
  setTemplates,
  listCells,
  hasCells,
  getMission,
  upsertCell,
} from '../domain/model';
import { missionAlias,
  mintId,
  UNTITLED,
  HEARTBEAT_STATUSES,
  CELL_WORKFLOWS_ALIAS,
  WORKSPACES_ALIAS
} from '../domain/schema';
import { rememberMission, rememberedMission } from './rememberedMission';

/**
 * MissionsNode — the Mission Control widget (`app.missions.control`).
 *
 * This is the former Mission Control container MINUS the tab shell: it owns the
 * mission store, the soft + serialized reload(), the session-content listener,
 * and applyLocal, and renders the cells surface (CellsSurface) directly — no
 * surface tabs.
 *
 * The mission-scoped controls it owns:
 *   - "New Cell" and "New Mission" are both the surface's own — New Cell floats over the board beside
 *     the view toggle, New Mission sits in the rail — and this node owns their handlers and the gate
 *     on each. Both create straight away, with no dialog.
 *   - Missions are chosen in the rail (surfaces/cells/missionsRail.js), driven by CellsSurface from the
 *     store.
 *
 * Data flow — ONE mission is on screen, and one mission is what this asks the session for:
 *   - On mount, and on session change (componentDidUpdate): reload() — mission ids from the index,
 *     one mission chosen (chooseMission) and swept (sweepMission)
 *   - On a store tick: handleStoreTick reacts narrowly to what actually moved
 *   - On a session_graph_changed for the mission on screen: one coalesced re-sweep of it
 */
class MissionsNode extends WorkNodeBase {
  constructor(props) {
    super(props);
    this.state = {
      store: createStore(),
      selection: null,
      loading: false,
      // What `loading` is waiting FOR, so the board can name it. Written by every site that sets
      // `loading` true and read nowhere else, because a reader only asks the question while the
      // spinner is up. Derived in the surface it would be a guess: the two waits are told apart by
      // whether a mission is selected, and `select()` is a batched setState that the `loading: true`
      // beside it can outrun (see State Management in CLAUDE.md — the same tick).
      loadingWhat: null,
      error: null,

      // A creation in flight, and what the last one said when it failed. One strip, above the surface,
      // because both gestures start from different places and neither has a dialog to report into.
      creatingMission: false,
      creatingCell: false,
      createError: null,
    };
    this._lastSessionId = null;
    this._seenGeneration = null;

    // Stable handler identities — bound once so the CellsSurface → panel prop chain
    // stays referentially stable across renders (keeps the mission graph's memoised
    // nodes/edges from churning on unrelated re-renders).
    this.applyLocal = this.applyLocal.bind(this);
    this.handleSelect = this.handleSelect.bind(this);

    this._watchedMission = null;   // whose graph events we hear
    // missionId -> the tag of the last sweep MADE for it. One entry per mission ever swept — the session's
    // own mission count is the bound — and cleared whole on a session change.
    this._sweeps = new Map();
    this._countSweep = null;       // the rail's own sequential count sweep — null when none is running
    this._graphTimer = null;
    // A clean slate bumps this, so a reload issued for the session being left cannot take the new one's
    // serialization flags with it when it finally settles.
    this._reloadEpoch = 0;
    this._consumed = { idsKey: null, envelopes: {}, wf: undefined, ws: undefined };
  }

  // ---------------------------------------------------------------------------
  // Stable handlers passed down to CellsSurface
  // ---------------------------------------------------------------------------

  /**
   * Apply a pure store updater locally without a server round-trip. Used by every
   * in-place write (add/remove/move cell, add/remove dependency, edit props) so
   * the graph updates incrementally — never a full reload + re-layout.
   * @param {(store: Object) => Object} updater
   */
  applyLocal(updater) {
    if (this._mounted) {
      this.setState(s => ({ store: updater(s.store) }));
    }
  }

  // Every path a person moves the selection by. A change of MISSION sweeps the new one: it was not kept
  // fresh while nobody looked at it, so what the store holds for it is old or absent. A change of cell
  // within the mission sweeps nothing.
  handleSelect(item) {
    const missionId = (item && item.missionId) || null;
    const changed = missionId !== this.selectedMissionId();
    // A creation error belongs to the moment it happened; it does not outlive a move.
    if (this.state.createError) this.setState({ createError: null });
    this.select(item);
    if (changed && missionId) this.sweepMission(missionId);
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  componentDidMount() {
    // Base builds the client (getClient) and sets up the WidgetBase forceUpdate listener.
    super.componentDidMount();
    this._lastSessionId = this._getSessionId();
    this._seenGeneration = this._storeGeneration();

    // The store's coalesced tick — "something in this session moved". The base class registers a
    // forceUpdate listener for rendering; this one is how a change to the DATA reaches the store,
    // narrowly (handleStoreTick).
    this._sessionContentListener = () => this.handleStoreTick();
    if (this.context) {
      this.context.addListener(this._sessionContentListener);
    }

    this.requestReload();
  }

  // The store's reset counter. Selecting the session already selected is an ordinary act — react-select fires
  // `onChange` for it — and it resets the store and drops every graph listener while the id stands still, so the
  // id alone cannot say when a clean slate is owed.
  _storeGeneration() {
    return this.context && this.context.objects ? this.context.objects.generation : null;
  }

  componentDidUpdate() {
    super.componentDidUpdate();
    // Detect session change and re-fetch when it does.
    const sessionId = this._getSessionId();
    const generation = this._storeGeneration();
    if (sessionId !== this._lastSessionId || generation !== this._seenGeneration) {
      this._lastSessionId = sessionId;
      this._seenGeneration = generation;
      // A session selected is a clean slate. The manager dropped every graph listener with the old one; the same
      // alias in another session is a DIFFERENT object, so the old session's cells must not survive; and
      // clearing the sweep tags is what stops a sweep still in flight from landing into the new store.
      this._watchedMission = null;
      this._sweeps.clear();
      // A graph event of the old session, still coalescing: left to fire, it sweeps whatever is on screen by then.
      if (this._graphTimer) clearTimeout(this._graphTimer);
      this._graphTimer = null;
      // The count sweep snapshots mission ids once and loops with its own `await`s — left running, it
      // would keep asking for the OLD session's ids after this point.
      this._countSweep = null;
      this.setState({
        store: createStore(), selection: null, loading: false, error: null,
      });
      // The reload of the session being left is abandoned here rather than waited for: `requestReload` honours
      // `_reloading`, and a sweep that landed on a cold Lambda holds it for seconds the new board must not wait.
      this._reloadEpoch++;
      this._reloading = false;
      this._reloadDirty = false;
      // That `selection: null` is still pending when reload() runs below, so reload is TOLD what is
      // selected rather than left to read a value React has not applied yet.
      this.requestReload({ selected: null });
    }
  }

  /**
   * Sync stays on while the orchestrator has something to do.
   *
   * A mission board is the one screen where nothing changes because of anything YOU did: cells
   * advance a minute at a time, on the heartbeat, and the whole point of watching is to see it
   * happen. Sync used to wind down after three minutes of that and leave a board that looked live
   * and was three minutes stale.
   *
   * Scoped to the mission on screen, because that is the one whose cells are drawn — polling for a
   * mission nobody is looking at is cost with nothing to show for it. `HEARTBEAT_STATUSES` is the
   * same three the orchestrator's own query sees, so the widget watches exactly while it does.
   */
  syncHold() {
    const missionId = this.state.selection && this.state.selection.missionId;
    if (!missionId) return null;
    const cells = listCells(this.state.store, missionId);
    // An ask nobody has picked up yet. `intent_pending` is the orchestrator's own signal that a
    // person asked for something and the tick has not applied it — up to a minute of a board that
    // must not go still, and the cell is not `working` yet so the count below does not see it.
    const asked = cells.filter(cell => (cell.props || {}).intent_pending).length;
    const live = cells
      .filter(cell => HEARTBEAT_STATUSES.includes((cell.props || {}).status)).length;
    if (asked > 0) return `Missions — ${asked} ask${asked === 1 ? '' : 's'} waiting to be picked up`;
    if (live === 0) return null;
    return `Missions — ${live} live cell${live === 1 ? '' : 's'}`;
  }

  componentWillUnmount() {
    if (this._graphTimer) clearTimeout(this._graphTimer);
    this._graphTimer = null;
    this._countSweep = null;
    this.watchMission(null);
    if (this.context && this._sessionContentListener) {
      this.context.removeListener(this._sessionContentListener);
    }
    this._sessionContentListener = null;
    super.componentWillUnmount();
  }

  // A tick says only that SOMETHING in the store moved — any object in the session raises one. This widget
  // cares about three things and reacts to each narrowly; anything else — another mission's envelope, an
  // artifact, a thread — costs nothing here. A graph change is the fourth, and arrives as its own event.
  handleStoreTick = () => {
    if (!this._mounted || !this.context || !this.context.session) return;
    const client = this.getClient();
    const objects = client.objects();
    const consumed = this._consumed;

    const ids = client.missionIds();
    const idsKey = ids.join('|');
    if (idsKey !== consumed.idsKey) {
      const selected = this.selectedMissionId();
      if (selected && ids.includes(selected)) {
        // The mission on screen is still here: repaint in place — it was not touched, so no sweep. The templates
        // are repainted with it because `noteConsumed` below counts them as consumed: a configuration object that
        // landed in the same tick window would otherwise never be painted, and no later tick would repair it.
        const missions = client.missions();
        const templates = client.templates();
        this.setState(s => ({ store: setTemplates(setMissions(s.store, missions), templates) }));
        this.sweepMissionCounts();
        this.noteConsumed(missions);
      } else {
        this.requestReload();    // nothing selected, or the selection vanished: choose again, and sweep the choice
      }
      return;
    }

    // The board is holding the spinner for a list that had not landed (reload()). A session with no missions
    // raises no other change a tick can see — its ids stay empty — so this is the only signal that clears it.
    if (this.state.loading && !this.selectedMissionId() && !this._listPending()) {
      this.requestReload();
      return;
    }

    // The VALUE, not the entry: a refresh replaces the entry object the moment it starts while the value it
    // holds stays put, so comparing entries would repaint on every fresh read of an unchanged object.
    // EVERY mission's envelope, not the selected one's alone: the rail states each mission's title and status.
    const envelopes = this._envelopeValues(ids);
    const wf = objects.peek(CELL_WORKFLOWS_ALIAS).value;
    const ws = objects.peek(WORKSPACES_ALIAS).value;
    const sameEnvelopes = ids.every(id => envelopes[id] === consumed.envelopes[id]);
    if (sameEnvelopes && wf === consumed.wf && ws === consumed.ws) return;

    // Metadata only — a title, a workspace, a palette. Not a graph change, so nothing is swept.
    this._consumed = { ...consumed, envelopes, wf, ws };
    const missions = client.missions();
    const templates = client.templates();
    this.setState(s => ({ store: setTemplates(setMissions(s.store, missions), templates) }));
  };

  // Is the session's object list still on its way? A FAILED list is an answer, terminal and not coming back,
  // so it counts as landed — an empty board says more there than a spinner nothing would ever clear.
  _listPending() {
    const objects = this.getClient().objects();
    return 'ready' !== objects.listStatus && 'error' !== objects.listStatus;
  }

  // The one door into reload(), so `_reloading` guards every entry.
  requestReload(options) {
    if (!this._mounted) return;
    // A reload already in flight is marked dirty instead — it re-runs once when it finishes, and by then
    // state is flushed, so the dropped `options` are exactly what the re-run would read for itself.
    if (this._reloading) { this._reloadDirty = true; return; }
    this.reload(options);
  }

  // ---------------------------------------------------------------------------
  // What is on screen — the one mission this widget asks the session for
  // ---------------------------------------------------------------------------

  selectedMissionId() {
    return (this.state.selection && this.state.selection.missionId) || null;
  }

  // The mission to show, decided BEFORE anything is swept: the selection if the session still has it, else
  // the one remembered for this session, else the first in row order. `current` is a parameter because a
  // caller mid-session-change knows the selection is being cleared before React has applied it.
  chooseMission(missionIds, current = this.selectedMissionId()) {
    if (current && missionIds.includes(current)) return current;
    const remembered = rememberedMission(this.props.data.shellInterface.getScope(), this._getSessionId());
    if (remembered && missionIds.includes(remembered)) return remembered;
    return missionIds[0] || null;
  }

  // Moves the selection. A change of MISSION moves the graph listener with it and, when it was the person's
  // choice, remembers it; a change of cell within the mission does neither.
  select(item, { remember = true } = {}) {
    const before = this.selectedMissionId();
    const after = (item && item.missionId) || null;
    this.setState({ selection: item });
    if (after === before) return;
    this.watchMission(after);
    if (remember && after) {
      rememberMission(this.props.data.shellInterface.getScope(), this._getSessionId(), after);
    }
  }

  // One listener, on the mission on screen. Its graph events re-sweep it, once per poll batch.
  watchMission(missionId) {
    if (this._watchedMission === missionId) return;
    const client = this.getClient();
    if (this._watchedMission) client.unwatchGraph(missionAlias(this._watchedMission), this.handleGraphChange);
    this._watchedMission = missionId || null;
    if (missionId) client.watchGraph(missionAlias(missionId), this.handleGraphChange);
  }

  // One poll delivers a batch of events and this fires once per event; collapse the batch onto one sweep.
  // The payload is ignored on purpose: whatever an event named, the mission on screen is what gets re-swept.
  handleGraphChange = () => {
    if (this._graphTimer) return;
    this._graphTimer = setTimeout(() => {
      this._graphTimer = null;
      if (this._mounted && this._watchedMission) this.sweepMission(this._watchedMission);
    }, 0);
  };

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  async reload({ selected = this.selectedMissionId() } = {}) {
    if (!this._mounted) return;
    if (!this.context || !this.context.session) {
      // No session — reset to empty store, no error
      this.setState({ store: createStore(), selection: null, loading: false, error: null });
      return;
    }
    const client = this.getClient();

    // Serialize reloads: a change arriving mid-reload sets _reloadDirty so the final state is re-fetched once.
    const epoch = this._reloadEpoch;
    this._reloading = true;
    this._reloadDirty = false;
    try {
      const missions = client.missions();
      const templates = client.templates();
      const missionId = this.chooseMission(missions.map(m => m.id), selected);
      this.setState(s => ({ store: setTemplates(setMissions(s.store, missions), templates), error: null }));
      this.noteConsumed(missions);
      if (missionId !== selected) {
        // No missions left to show is a selection of NOTHING, not a selection of `null` wrapped in an item.
        this.select(missionId ? { missionId, cellId: null, edge: null } : null, { remember: false });
      }
      // Idempotent, and not redundant with the select above: a session change resets _watchedMission
      // (componentDidUpdate) while the chosen mission can come out the same, and this re-establishes it.
      this.watchMission(missionId);
      if (missionId) {
        await this.sweepMission(missionId);
        // The rail's counts, once the board is up. One mission at a time, under its own run token,
        // skipped for anything the store already holds — see sweepMissionCounts.
        this.sweepMissionCounts();
      } else {
        // Nothing to choose from is not the same fact as nothing to show: the index proves presence, never
        // absence, so until the list has landed "No missions yet." is an answer the store has not got.
        this.setState({ loading: this._listPending() && 0 === client.objects().rows().length,
                        loadingWhat: LOADING_MISSIONS });
      }
    } catch (err) {
      if (this._mounted) {
        const message = err && err.message ? err.message : 'Failed to load Mission Control data.';
        this.setState({ loading: false, error: message });
      }
    } finally {
      // Both flags belong to whatever reload is current. This one settling after a clean slate speaks for a
      // session that has been left, so it hands back nothing — the reload that replaced it owns them now.
      if (epoch === this._reloadEpoch) {
        this._reloading = false;
        // A content change arrived while we were loading — re-fetch once to converge on the latest state.
        if (this._mounted && this._reloadDirty) {
          this._reloadDirty = false;
          this.requestReload();
        }
      }
    }
  }

  // missionId -> the envelope's VALUE as the session holds it now.
  _envelopeValues(ids) {
    const objects = this.context.objects;
    return Object.fromEntries(ids.map(id => [id, objects.peek(missionAlias(id)).value]));
  }

  // What the last reload consumed, so a tick can tell whether anything it cares about moved.
  noteConsumed(missions) {
    const objects = this.context.objects;
    this._consumed = {
      idsKey: missions.map(m => m.id).join('|'),
      envelopes: this._envelopeValues(missions.map(m => m.id)),
      wf: objects.peek(CELL_WORKFLOWS_ALIAS).value,
      ws: objects.peek(WORKSPACES_ALIAS).value,
    };
  }

  // Cells and edges of ONE mission, folded into the store. The last sweep MADE for a mission wins over one still
  // in flight — a switch away and back, or a graph event during a sweep, must not land stale cells.
  async sweepMission(missionId) {
    const client = this.getClient();
    const sweep = {};
    this._sweeps.set(missionId, sweep);
    const selectedOf = s => (s.selection && s.selection.missionId) || null;
    // Only the mission ON SCREEN may touch `loading` or `error`, and `loading` only to cover an EMPTY board —
    // never cells already up, and never a sweep of some other mission (the rail's counts). Read through a
    // functional updater, because the selection this sweep follows is often set in the same batch, still pending.
    this.setState(s => (selectedOf(s) === missionId
      ? { loading: !hasCells(s.store, missionId), loadingWhat: LOADING_CELLS }
      : { loading: s.loading, loadingWhat: s.loadingWhat }));
    // The listings are a snapshot of the moment they were issued, and `setCells` below replaces the
    // mission wholesale — so a write of ours that started behind them makes them pre-write, and landing
    // one would revert what the board already shows. Its own echo is dropped, so nothing else would.
    const seq = client.writeSeq;
    try {
      const [cells, edges] = await Promise.all([client.listCells(missionId), client.listDependsOn(missionId)]);
      if (!this._mounted || this._sweeps.get(missionId) !== sweep) return;
      if (client.writeSeq !== seq) {
        // Discarded whole — `loading` and `error` still describe the sweep this one is about to repeat.
        await client.whenWritesSettled();
        if (!this._mounted || this._sweeps.get(missionId) !== sweep) return;
        return this.sweepMission(missionId);
      }
      this.setState(s => ({
        store: setDependsOn(setCells(s.store, missionId, cells || []), missionId, edges || []),
        loading: selectedOf(s) === missionId ? false : s.loading,
        error: selectedOf(s) === missionId ? null : s.error,
      }));
    } catch (err) {
      if (!this._mounted || this._sweeps.get(missionId) !== sweep) return;
      const message = err && err.message ? err.message : 'Failed to load Mission Control data.';
      // A failed sweep keeps what is on screen. The listings reject rather than answering `[]` (client.js),
      // so this is where a real failure lands, and folding one in would empty a board that nothing refills
      // until the next graph event. Always logged; it only TAKES the screen when there is nothing to leave up.
      console.error(`Mission Control: sweep of mission ${missionId} failed`, err);
      this.setState(s => ({
        loading: selectedOf(s) === missionId ? false : s.loading,
        error: selectedOf(s) === missionId && !hasCells(s.store, missionId) ? message : s.error,
      }));
    }
  }

  // ---------------------------------------------------------------------------
  // The rail's counts
  // ---------------------------------------------------------------------------

  // Counts for the rail's rows — the one thing that still costs a sweep per mission — one mission at a
  // time. A count learned once is kept. Started by reload() once the board is up, and by a change
  // to the mission set; a run already going is left to finish rather than restarted.
  async sweepMissionCounts() {
    if (this._countSweep) return;
    const run = {};
    this._countSweep = run;
    for (const id of this.getClient().missionIds()) {
      if (!this._mounted || this._countSweep !== run) return;
      // The mission on screen is the board's own sweep: repeating it here would turn a failed board's error
      // into a spinner, and an empty one into a second request.
      if (id === this.selectedMissionId() || hasCells(this.state.store, id)) continue;
      await this.sweepMission(id);
    }
    if (this._countSweep === run) this._countSweep = null;
  }

  // ---------------------------------------------------------------------------
  // Creation — mints, selects, asks nothing
  // ---------------------------------------------------------------------------

  handleCreateMission = async () => {
    // Two presses would mint two ids and so two missions, and nothing here can undo one.
    if (!this.canEdit() || this.state.creatingMission || !this._client) return;
    this.setState({ creatingMission: true, createError: null });
    const id = mintId();
    try {
      await this._client.createMission({ id, title: UNTITLED, description: '' });
      // Creating a mission is choosing it: the same act as picking one, so it goes through the same
      // door — selected, watched, remembered and swept. Its row arrives with the index, on a tick.
      this.handleSelect({ missionId: id, cellId: null, edge: null });
    } catch (e) {
      this.setState({ createError: e && e.message ? e.message : 'Failed to create mission.' });
    } finally {
      if (this._mounted) this.setState({ creatingMission: false });
    }
  };

  // Whether the board is offered a door to a new cell at all. The mission has to EXIST, not merely be
  // named: a selection outlives what it points at — one deleted or archived out from under it, a stale
  // one restored on load — and keyed on the id alone the control stayed, where pressing it asked the
  // server to add a cell to a graph object that is not there. It is read where the prop is handed over
  // (renderBody), because that prop's presence IS the gate the surface draws by.
  canAddCell() {
    const missionId = this.selectedMissionId();
    return this.canEdit() && !!(missionId && getMission(this.state.store, missionId));
  }

  handleAddCell = async () => {
    const missionId = this.selectedMissionId();
    // Two presses would mint two cells; the dialog's busy state used to stop that.
    if (!this.canEdit() || !missionId || !this._client || this.state.creatingCell) return;
    this.setState({ creatingCell: true, createError: null });
    try {
      // Root cells (added here, not via the graph) default both structural modes to inherit-from-mission.
      const cell = await this._client.addCell(missionId, {
        title: UNTITLED,
        workspace_mode: 'inherit_from_mission',
        cell_workflow_mode: 'inherit_from_mission',
      });
      // Incremental insert — no full reload / re-layout — and then the selection, which is what puts
      // the dock on the new cell's Details.
      this.applyLocal(s => upsertCell(s, missionId, cell));
      this.handleSelect({ missionId, cellId: cell.element_id, edge: null });
    } catch (e) {
      this.setState({ createError: e && e.message ? e.message : 'Failed to add a cell.' });
    } finally {
      if (this._mounted) this.setState({ creatingCell: false });
    }
  };

  // ---------------------------------------------------------------------------
  // WidgetBase overrides
  // ---------------------------------------------------------------------------

  getClassName() {
    return 'playground-work-node playground-missions-node';
  }

  // The selected mission shown in the header, styled exactly like the graph/thread widgets'
  // bound-object alias (its session-object alias `missions/<mid>/graph`).
  renderHeaderAlias() {
    const missionId = this.state.selection && this.state.selection.missionId;
    if (!missionId) return null;
    return <Alias value={missionAlias(missionId)} variant="chip" />;
  }

  renderBody() {
    const {
      store, selection, loading, loadingWhat, error,
      createError,
    } = this.state;

    const selectedMissionId = selection && selection.missionId;

    // What this widget reads on every paint — reading is what asks (session/aliasWatch.js): the two
    // configuration objects, the mission on screen, and EVERY mission's envelope, because the rail
    // states every mission's title and status. One small object each, fetched once per session.
    for (const alias of this.getClient().baseAliases(selectedMissionId)) this.objects.read(alias);
    for (const alias of this.getClient().missionAliases()) this.objects.read(alias);

    return (
      <div className="mission-control-shell">
        {createError && (
          <WidgetStatus variant="warning">
            {createError}
            <button type="button" className="close" aria-label="Dismiss error"
              onClick={() => this.setState({ createError: null })}>
              <span aria-hidden="true">&times;</span>
            </button>
          </WidgetStatus>
        )}

        <div className="mission-control-surface-body">
          {/* Always mounted: the rail is navigation, and a board that is loading or has failed is exactly
              when a person needs another mission or + New Mission. The surface draws the status itself. */}
          <CellsSurface
            store={store}
            client={this._client}
            canEdit={this.canEdit()}
            selection={selection}
            loading={loading}
            loadingWhat={loadingWhat}
            hasSession={!!(this.context && this.context.session)}
            error={error}
            onSelect={this.handleSelect}
            onApplyLocal={this.applyLocal}
            onCreateMission={this.handleCreateMission}
            creatingMission={this.state.creatingMission}
            onCreateCell={this.canAddCell() ? this.handleAddCell : undefined}
            creatingCell={this.state.creatingCell}
            scope={this.props.data.shellInterface.getScope()}
          />
        </div>
      </div>
    );
  }
}

export default MissionsNode;
